'use client'
import AppliedFilters from '@/app/components/AppliedFilters'
import Text from '@/app/components/common/Text'
import Title from '@/app/components/common/Title'
import FiltersPlaceholder from '@/app/components/FiltersPlaceholder'
import HotelListPlaceholder from '@/app/components/HotelsListPlaceholder'
import HotelsLoadingAnimation from '@/app/components/HotelsLoadingAnimation'
import ResultsFilters from '@/app/components/ResultsFilters'
import SortingAndFilters from '@/app/components/SortingAndFilters'
import { Icons } from '@/app/components/ui/Icons'
import {
	DEFAULT_CHECK_IN_DATE,
	DEFAULT_CHECK_OUT_DATE,
	DEFAULT_ROOMS_AND_GUESTS,
	EMPTY_HOTELS_RESPONSE,
} from '@/app/constants'
import {
	emitRpNoHotelsFoundFiltersGAEvent,
	emitRpNoHotelsFoundGAEvent,
	emitRpNoSpecificHotelGAEvent,
	emitRpResetAllFiltersGAEvent,
} from '@/app/ga-event-helpers'
import { useFallbackHotelsQuery } from '@/app/hooks/queries/useFallbackHotelsQuery'
import { useHotelsQuery } from '@/app/hooks/queries/useHotelsQuery'
import { useSimilarHotelsQuery } from '@/app/hooks/queries/useSimilarHotelsQuery'
import useIsGAReady from '@/app/hooks/useIsGAReady'
import { useUserData } from '@/app/hooks/useUserData'
import {
	DestPagesProps,
	FiltersCountMap,
	HotelsListResponse,
} from '@/app/types'
import {
	capitalize,
	determineSearchPlaceFromPath,
	getAppliedFiltersFromURL,
	isAirport,
	isHood,
	isLandmark,
	parseSearchParamsToLower,
} from '@/app/utils'
import classNames from 'classnames'
import { camelCase, startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import HotelsList from './HotelsList'

export interface Props extends DestPagesProps {}

/**
 * Hotels Component
 *
 * Error Handling Strategy:
 * 1. useUserData hook handles localStorage errors gracefully with fallback data
 * 2. Component shows loading state while userData is being loaded
 * 3. If userData fails to load, a warning is logged but the app continues with fallback data
 * 4. The parent component (HotelsWithErrorBoundary) catches any unexpected errors
 *
 * This ensures the app remains functional even if:
 * - localStorage is blocked (private browsing)
 * - userData is corrupted
 * - localStorage quota is exceeded
 */
const Hotels = (props: Props) => {
	const { dest } = props.params
	const [country, region, city, hotel, place] = dest
	const {
		locationName,
		s,
		gr,
		p,
		fa,
		t: themesParam,
		mx,
		mn,
		page,
	} = props.searchParams
	const fallbackLocation = dest[dest.length - 1]
	const displayLocation = locationName
		? decodeURIComponent(locationName as string)
		: startCase(camelCase(decodeURIComponent(fallbackLocation)))
	const {
		userData,
		isLoading: isUserDataLoading,
		error: userDataError,
	} = useUserData()

	const router = useRouter()
	const queryParams = useSearchParams()
	const t = useTranslations('Keywords')
	const isGAReady = useIsGAReady()

	const startTimeRef = useRef<number>(Date.now())
	const [hotelsState, setHotelsState] = useState<HotelsListResponse | null>(
		null
	)
	const [shouldRunFallbackQuery, setShouldRunFallbackQuery] = useState(false)
	const appliedFilters = getAppliedFiltersFromURL(queryParams)
	const isSingleHotelRequest = Boolean(props.searchParams.hotelID)
	const prevDestRef = useRef<string>('')
	const prevSearchParamsRef = useRef<string>('')
	const isLandmarkSearch = isLandmark(hotel)

	const hasFilters = !!(s || gr || p || fa || themesParam || mx || mn)
	const shouldFetchSimilarData =
		!!city && !isAirport(city) && !!hotel && !isLandmarkSearch && !isHood(hotel)
	const [isFetchingComplete, setIsFetchingComplete] = useState(false)
	const [isFallbackFetchingComplete, setIsFallbackFetchingComplete] =
		useState(false)
	const [isSimilarFetchingComplete, setIsSimilarFetchingComplete] =
		useState(false)
	const [useSimilarStarsFilter, setUseSimilarStarsFilter] = useState(true)
	const searchParams = parseSearchParamsToLower(props.searchParams)
	const placeID = searchParams?.['placeid']
	const hotelID = searchParams?.['hotelid']
	const sessionID = searchParams?.['sessionid']
	const uid = searchParams?.['uid']
	const hasAllowedPartners = !!searchParams?.['ap']

	const renderTitle = () => {
		if (isLandmarkSearch) {
			return `${displayLocation} ${t('bestHotels')}`
		}
		return `${capitalize(t('bestHotelsIn'))} ${displayLocation}`
	}

	useEffect(() => {
		const currentDest = props.params.dest.join(',')
		const currentSearchParams = JSON.stringify(props.searchParams)

		if (
			prevDestRef.current !== currentDest ||
			prevSearchParamsRef.current !== currentSearchParams
		) {
			startTimeRef.current = Date.now()
			prevDestRef.current = currentDest
			prevSearchParamsRef.current = currentSearchParams
		}
	}, [props.params.dest, props.searchParams])

	const {
		data: hotelsData,
		isLoading: isLoadingHotels,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useHotelsQuery({
		props,
		startTimeRef,
		setIsFetchingComplete,
		userData,
	})

	useEffect(() => {
		if (hotelsData?.pages && hotelsData.pages.length > 0) {
			const lastPage = hotelsData.pages[hotelsData.pages.length - 1]
			const singleHotelNoAvailability =
				isSingleHotelRequest &&
				hotelsData.pages[0].hotels[0].rawHotelData.bestRate.availableRooms === 0
			setHotelsState(lastPage || null)

			const placeType = determineSearchPlaceFromPath(props.params.dest, false)

			const dates = {
				'check-in-date':
					props.searchParams['check-in-date'] || DEFAULT_CHECK_IN_DATE,
				'check-out-date':
					props.searchParams['check-out-date'] || DEFAULT_CHECK_OUT_DATE,
			}
			const room = props.searchParams.room || DEFAULT_ROOMS_AND_GUESTS
			const eventParams = {
				...props.searchParams,
				...dates,
				room,
				loctype: placeType.type,
			}

			if (
				hotelsData.pages[0].totalResults === 0 &&
				!isSingleHotelRequest &&
				isGAReady
			) {
				if (hasFilters) {
					emitRpNoHotelsFoundFiltersGAEvent(eventParams)
				} else {
					emitRpNoHotelsFoundGAEvent(eventParams)
				}
			}

			if (singleHotelNoAvailability && isGAReady) {
				emitRpNoSpecificHotelGAEvent(eventParams)
			}
		}
	}, [hotelsData, hasFilters, isGAReady, isSingleHotelRequest])

	const data: HotelsListResponse = hotelsState || EMPTY_HOTELS_RESPONSE

	useEffect(() => {
		if (hotelsData?.pages && hotelsData.pages.length > 0) {
			const lastPage = hotelsData.pages[hotelsData.pages.length - 1]
			setHotelsState(lastPage || null)

			// Check if we should run the fallback query
			if (lastPage && lastPage.hotels.length === 0) {
				setShouldRunFallbackQuery(true)
			} else {
				setShouldRunFallbackQuery(false)
			}
		}
	}, [hotelsData, hasFilters])

	const {
		data: similarHotelsData,
		isLoading: similarHotelsLoading,
		fetchNextPage: fetchNextSimilarPage,
		hasNextPage: hasNextSimilarPage,
		isFetchingNextPage: isFetchingNextSimilarPage,
		isSuccess: isSimilarSuccess,
	} = useSimilarHotelsQuery({
		props,
		country,
		region,
		city,
		s,
		hotelID,
		placeID,
		useSimilarStarsFilter,
		shouldFetchSimilarData,
		data,
		startTimeRef,
		setIsSimilarFetchingComplete,
		userData,
	})

	const similarData = similarHotelsData?.pages[0] || null

	useEffect(() => {
		if (
			isSimilarSuccess &&
			useSimilarStarsFilter &&
			similarHotelsData?.pages &&
			similarHotelsData.pages[0]?.hotels.length === 0 &&
			isSimilarFetchingComplete
		) {
			setUseSimilarStarsFilter(false)
		}
	}, [
		isSimilarSuccess,
		similarHotelsData,
		useSimilarStarsFilter,
		isSimilarFetchingComplete,
	])

	const {
		data: fallbackHotelsData,
		isLoading: isFallbackLoading,
		fetchNextPage: fetchNextFallbackPage,
		hasNextPage: hasNextFallbackPage,
		isFetchingNextPage: isFetchingNextFallbackPage,
	} = useFallbackHotelsQuery({
		props,
		shouldRunFallbackQuery,
		startTimeRef,
		setIsFallbackFetchingComplete,
		userData,
	})

	const handleResetFilters = () => {
		emitRpResetAllFiltersGAEvent()

		const { s, gr, fa, p, t, mn, mx, page, ...restParams } = props.searchParams

		const searchParams = new URLSearchParams(
			Object.fromEntries(
				Object.entries(restParams).map(([key, value]) => [
					key,
					Array.isArray(value) ? value.join(',') : value,
				])
			)
		)

		const newUrl = `${window.location.pathname}?${searchParams.toString()}`
		router.push(newUrl)
	}

	// Show loading state while userData is loading or hotels are loading
	if (
		isUserDataLoading ||
		!userData ||
		(isLoadingHotels && !data.hotels.length)
	) {
		return (
			<div className="container flex justify-center">
				{!isSingleHotelRequest && (
					<div
						className={classNames(
							'w-[232px] xl:w-[266px] flex-shrink-0 xl:block hidden',
							{
								'me-5': !isSingleHotelRequest,
							}
						)}
					>
						<FiltersPlaceholder t={t} />
					</div>
				)}
				<div className="w-full xl:max-w-[calc(100%-300px)]">
					<HotelListPlaceholder />
				</div>
			</div>
		)
	}

	// Show error message if userData failed to load but continue with fallback data
	if (userDataError) {
		console.warn('Failed to load user data, using fallback:', userDataError)
	}

	return (
		<div className="container mx-auto lg:mt-6 max-lg:pb-5">
			<div
				className={classNames('flex', {
					'justify-between': !isSingleHotelRequest,
					'justify-center': isSingleHotelRequest,
				})}
			>
				{/* Sidebar */}
				{!isSingleHotelRequest && (
					<div
						className={classNames(
							'w-[232px] xl:w-[266px] flex-shrink-0 xl:block hidden'
						)}
					>
						{data.filters && (
							<>
								<div className="flex justify-start items-center mb-5 lg:h-[42px]">
									<Title level="3" size="xl" color="text-900" bold>
										{t('filterBy')}
									</Title>
								</div>
								<ResultsFilters
									pageProps={props}
									availableRates={{
										min: data.lowestNightlyRate,
										max: data.highestNightlyRate,
									}}
									availableFilters={data.filters as FiltersCountMap}
								/>
							</>
						)}
					</div>
				)}

				{/* Main content */}
				<div className="flex-grow w-full xl:max-w-[calc(100%-300px)]">
					<div
						className={classNames(
							'overflow-hidden transition-all h-0 delay-150 duration-300',
							{
								'h-[110px]': !isFetchingComplete,
							}
						)}
					>
						<HotelsLoadingAnimation />
					</div>
					{!data.hotels.length && hasFilters && (
						<>
							<div className="block lg:hidden">
								<SortingAndFilters
									pageProps={props}
									availableRates={{
										min: data.lowestNightlyRate,
										max: data.highestNightlyRate,
									}}
									hasFilters={hasFilters}
									isSingleHotels={isSingleHotelRequest}
									availableFilters={data.filters as FiltersCountMap}
								/>
							</div>
							<div className="bg-text-200 lg:bg-text-100 max-lg:-ms-4 max-lg:-me-4 lg:rounded-4xl p-4 lg:p-6 mb-6">
								<div className="mb-4">
									<Title level="4" size="base" bold>
										{t('hotelsFound', {
											count: data.hotels.length,
											location: locationName as string,
										})}
									</Title>
									<Text className="text-text-900">{t('tryToReset')}</Text>
								</div>
								<div>
									<AppliedFilters
										appliedFilters={{
											...appliedFilters,
											// Price range filter logic:
											// If both min and max are 0, return undefined (no filter applied)
											// If min is 0, use data.lowestNightlyRate; otherwise, use the applied min
											// If max is 0, use data.highestNightlyRate; otherwise, use the applied max
											priceRange:
												appliedFilters.priceRange[0] === 0 &&
												appliedFilters.priceRange[1] === 0
													? undefined
													: [
															appliedFilters.priceRange[0] === 0
																? data.lowestNightlyRate
																: appliedFilters.priceRange[0],
															appliedFilters.priceRange[1] === 0
																? data.highestNightlyRate
																: appliedFilters.priceRange[1],
													  ],
										}}
										searchParams={queryParams}
									/>
									<button
										data-test-id="reset-filters-button"
										onClick={handleResetFilters}
										className={classNames(
											'px-4 py-2 rounded-full flex items-center w-fit bg-white border text-score-good font-bold border-score-good hover:bg-text-200 transition-colors'
										)}
									>
										{t('resetAllFilters')}
										<Icons.trashCan className="ms-2 max-lg:hidden" />
									</button>
								</div>
							</div>
						</>
					)}

					{
						data.hotels.length > 0 ? (
							<>
								{/* Header */}
								<div
									className={classNames(
										'flex flex-col items-start md:flex-row-reverse md:items-center justify-between w-full',
										{
											'mb-3 md:mb-4': !isSingleHotelRequest,
										}
									)}
								>
									<div
										className={classNames({
											hidden: isSingleHotelRequest,
										})}
									>
										<SortingAndFilters
											pageProps={props}
											availableRates={{
												min: data.lowestNightlyRate,
												max: data.highestNightlyRate,
											}}
											isSingleHotels={isSingleHotelRequest}
											availableFilters={data.filters as FiltersCountMap}
										/>
									</div>

									{!similarData && !isSingleHotelRequest && (
										<div
											className="flex-shrink-0 flex items-start max-md:items-center flex-1 basis-0 min-w-0 max-md:mt-2"
											data-test-id="results-title"
										>
											<h2 className="font-bold text-sm lg:text-lg text-text-900 truncate">
												{renderTitle()}
											</h2>
										</div>
									)}
								</div>

								{/* Main Hotels List */}
								<HotelsList
									lang={props.params.lang}
									pageParams={props}
									showLoadMore={hasNextPage && !isSingleHotelRequest}
									shouldLoadMore={!isSingleHotelRequest}
									data={data}
									fetchNextPage={fetchNextPage}
									hasNextPage={hasNextPage}
									isFetchingNextPage={isFetchingNextPage}
									allData={hotelsData}
									totalPages={data.pages}
									isSingleHotelRequest={isSingleHotelRequest}
									isLandmark={isLandmarkSearch}
									isComplete={isFetchingComplete}
								/>

								{/* Similar Hotels */}
								{(similarHotelsLoading || similarData) && (
									<div className="mt-2 md:mt-8">
										{similarData && similarData?.hotels?.length > 0 && (
											<div
												className="flex max-md:flex-col-reverse max-md:items-start items-center justify-between mb-4"
												data-test-id="similar-hotels-header"
											>
												<div className="font-bold text-sm max-md:mt-2 lg:text-lg text-text-900 leading-4">
													{t('hotelsSimilarTo')}
													<span className="mx-1">
														{decodeURIComponent(hotel)}
													</span>
												</div>
												{/* <div className="hidden md:block"> */}
												<SortingAndFilters
													pageProps={props}
													availableRates={{
														min: data.lowestTotalRate,
														max: data.highestTotalRate,
													}}
													isSingleHotels={isSingleHotelRequest}
													isSimilarHotels
													availableFilters={data.filters as FiltersCountMap}
												/>
												{/* </div> */}
											</div>
										)}
										{similarHotelsLoading ? (
											<HotelListPlaceholder />
										) : (
											similarData &&
											!!similarData.hotels.length && (
												// Similar hotels list
												<HotelsList
													lang={props.params.lang}
													data={similarData}
													fetchNextPage={fetchNextSimilarPage}
													hasNextPage={!!hasNextSimilarPage}
													isFetchingNextPage={isFetchingNextSimilarPage}
													allData={similarHotelsData}
													showLoadMore={!!hasNextSimilarPage}
													shouldLoadMore
													totalPages={similarData.pages}
													isComplete={isSimilarFetchingComplete}
													isSimilarData
												/>
											)
										)}
									</div>
								)}
							</>
						) : shouldRunFallbackQuery ? (
							<>
								<div className="md:hidden mb-4 min-h-10">
									<SortingAndFilters
										pageProps={props}
										availableRates={{
											min: data.lowestNightlyRate,
											max: data.highestNightlyRate,
										}}
										isFallbackData
										availableFilters={data.filters as FiltersCountMap}
									/>
								</div>
								{!hasFilters && !hasAllowedPartners && (
									<div className="flex items-start p-4 mb-6 bg-text-200 max-lg:-mx-4 lg:p-6 lg:bg-text-100 lg:rounded-4xl">
										<Icons.iconAlert className="me-1.5 max-lg:hidden" />
										<div className="flex flex-col text-text-900">
											<span className="font-bold leading-5 max-lg:mb-1 lg:text-xl lg:leading-6">
												{t('hotelsSearch404', { location: displayLocation })}
											</span>
											<span className="leading-5 max-lg:text-sm">
												{t('tryChangeDates')}
											</span>
										</div>
									</div>
								)}
								{isFallbackLoading ? (
									<div>
										{/* <div className="mb-3 lg:mb-4">
											<div className="flex justify-between w-full max-md:flex-col-reverse items-start">
												<div className="font-bold text-sm lg:text-lg text-text-900">
													{hasAllowedPartners ? (
														<>
															<h2 className="font-bold text-sm lg:text-lg text-text-900 px-5">
																{renderTitle()}
															</h2>
														</>
													) : (
														t('dealsThatYouMightLike')
													)}
												</div>
											</div>
										</div> */}
										<HotelListPlaceholder />
									</div>
								) : fallbackHotelsData?.pages &&
								  fallbackHotelsData?.pages[0].totalResults > 0 ? (
									<div>
										<div className="mb-3 lg:mb-4">
											<div className="flex max-md:items-start justify-between w-full max-md:flex-col-reverse items-center">
												<div className="font-bold text-sm lg:text-lg text-text-900">
													{hasAllowedPartners ? (
														<>
															<h2 className="font-bold text-sm lg:text-lg text-text-900">
																{renderTitle()}
																<span className="ms-1">
																	({fallbackHotelsData.pages[0].totalResults})
																</span>
															</h2>
														</>
													) : (
														t('dealsThatYouMightLike')
													)}
												</div>
												<div className="hidden md:block">
													<SortingAndFilters
														pageProps={props}
														availableRates={{
															min: data.lowestNightlyRate,
															max: data.highestNightlyRate,
														}}
														availableFilters={data.filters as FiltersCountMap}
														isFallbackData
													/>
												</div>
											</div>
										</div>
										{/* Fallback hotel list */}
										<HotelsList
											lang={props.params.lang}
											pageParams={props}
											showLoadMore={!hotel}
											shouldLoadMore={!hotel}
											isComplete={isFallbackFetchingComplete}
											isFallbackData
											data={
												fallbackHotelsData.pages[
													fallbackHotelsData.pages.length - 1
												]
											}
											fetchNextPage={fetchNextFallbackPage}
											hasNextPage={hasNextFallbackPage}
											isFetchingNextPage={isFetchingNextFallbackPage}
											allData={fallbackHotelsData}
											totalPages={
												fallbackHotelsData.pages[
													fallbackHotelsData.pages.length - 1
												].pages
											}
										/>
									</div>
								) : null}
							</>
						) : null
						// <>
						// 	<div className="flex items-center justify-between max-md:mt-8 mb-6 w-full">
						// 		<div className="font-bold text-lg">
						// 			<span className="me-1">No hotels found</span>
						// 		</div>
						// 	</div>
						// 	<NotFound hasFilters={hasFilters} />
						// </>
					}
				</div>
			</div>
		</div>
	)
}

export default Hotels
